import Form, { InputValue } from "@bmi-digital/components/form";
import Grid from "@bmi-digital/components/grid";
import Section from "@bmi-digital/components/section";
import logger from "@bmi-digital/functions-logger";
import { Training } from "@bmi/elasticsearch-types";
import { navigate } from "gatsby";
import React, { useMemo } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { replaceSpaces } from "@bmi-digital/components/utils";
import type { TrainingRequest } from "@bmi/training-registration-types";
import { useSiteContext } from "../../../components/Site";
import { QA_AUTH_TOKEN } from "../../../constants/cookieConstants";
import { useConfig } from "../../../contexts/ConfigProvider";
import getCookie from "../../../utils/getCookie";
import { registrationMicrocopy } from "../registrationMicrocopy";
import { FormStatus, TrainingRegistrationPageData } from "../types";
import FormFields from "./FormFields";
import { StyledRecaptchaText } from "./TrainingRegistrationFormStyles";

type TrainingRegistrationFormProps = {
  formData: TrainingRegistrationPageData;
  setFormStatus: (formStatus: FormStatus) => void;
  trainingDetailsPageUrl: string;
  courseCode?: string;
  training?: Training;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
};

const getOptionalTrainingRequestValue = (
  key: string,
  values: Record<string, InputValue>
): string | undefined => {
  // eslint-disable-next-line security/detect-object-injection
  const value = values[key];
  return typeof value === "string" ? value : undefined;
};

const TrainingRegistrationForm = (props: TrainingRegistrationFormProps) => {
  const { getMicroCopy, node_locale } = useSiteContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const qaAuthToken = getCookie(QA_AUTH_TOKEN);
  const {
    gcpFormSubmitEndpoint,
    gcpTrainingRequestsWriterEndpoint,
    isPreviewMode
  } = useConfig();

  const discoverySourceOtherFieldName = useMemo(
    () => `${replaceSpaces(props.formData.discoverySourceOther)}-textfield`,
    [props.formData.discoverySourceOther]
  );

  const getHeaders = async (
    qaAuthToken: string | undefined
  ): Promise<HeadersInit> => {
    if (qaAuthToken) {
      return {
        "Content-Type": "application/json",
        authorization: `Bearer ${qaAuthToken}`
      };
    }

    const recaptchaToken = await executeRecaptcha?.();

    return recaptchaToken
      ? {
          "Content-Type": "application/json",
          "X-Recaptcha-Token": recaptchaToken
        }
      : {
          "Content-Type": "application/json"
        };
  };

  const handleFormSubmit = async (
    event: React.FormEvent,
    values: Record<string, InputValue>
  ) => {
    if (!props.training) {
      return;
    }

    event.preventDefault();
    props.setIsSubmitting(true);

    try {
      const valuesToSend = Object.entries(values).reduce<
        Record<string, unknown>
      >((acc: Record<string, InputValue>, [key, value]) => {
        if (value) {
          // eslint-disable-next-line security/detect-object-injection
          acc[key] = value;
        }
        return acc;
      }, {});

      const sanitizedValues: Record<string, string> = {};
      const checkmark = String.fromCodePoint(0x2713);

      sanitizedValues[getMicroCopy(registrationMicrocopy.emailLabel)] =
        `${props.training.courseCode} - ${props.training.courseName}, ${props.training.sessionName}`;

      sanitizedValues[getMicroCopy(registrationMicrocopy.emailStartDate)] =
        new Date(props.training.startDate).toLocaleString(node_locale);

      const discoverySourceValue = values[props.formData.discoverySourceTitle];
      const isOtherDiscoverySourceSelected =
        discoverySourceValue === props.formData.discoverySourceOther;

      sanitizedValues[getMicroCopy(registrationMicrocopy.emailTerms)] =
        checkmark;

      sanitizedValues[getMicroCopy(registrationMicrocopy.emailConsent)] =
        checkmark;

      for (const key of Object.keys(valuesToSend)) {
        if (key === "consent") {
          continue;
        }

        if (key === "terms-of-use") {
          continue;
        }

        if (key === discoverySourceOtherFieldName) {
          continue;
        }

        if (
          isOtherDiscoverySourceSelected &&
          key === props.formData.discoverySourceTitle
        ) {
          // eslint-disable-next-line security/detect-object-injection
          const value = valuesToSend[discoverySourceOtherFieldName];
          const trimmedValue = typeof value === "string" ? value.trim() : "";
          // eslint-disable-next-line security/detect-object-injection
          sanitizedValues[key] =
            trimmedValue || props.formData.discoverySourceOther;
          continue;
        }

        // eslint-disable-next-line security/detect-object-injection
        const value = valuesToSend[key];
        const trimmedValue = typeof value === "string" ? value.trim() : "";
        if (trimmedValue !== "") {
          // eslint-disable-next-line security/detect-object-injection
          sanitizedValues[key] = trimmedValue;
        }
      }

      if (gcpFormSubmitEndpoint) {
        const headers = await getHeaders(qaAuthToken);

        const formSubmitResponse = await fetch(gcpFormSubmitEndpoint, {
          method: "POST",
          body: JSON.stringify({
            locale: node_locale,
            title: "",
            recipients: props.formData.recipient,
            values: sanitizedValues,
            emailSubjectFormat: `${props.formData.emailSubject} ${props.courseCode}`
          }),
          headers
        });

        if (!formSubmitResponse.ok) {
          throw new Error(formSubmitResponse.statusText);
        }
      }

      if (gcpTrainingRequestsWriterEndpoint) {
        const headers = await getHeaders(qaAuthToken);

        const trainingRequest: TrainingRequest = {
          firstName: String(valuesToSend[props.formData.firstName]),
          lastName: String(valuesToSend[props.formData.lastName]),
          customerNumber: getOptionalTrainingRequestValue(
            props.formData.customerNumber,
            valuesToSend
          ),
          comment: getOptionalTrainingRequestValue(
            props.formData.comment,
            valuesToSend
          ),
          companyName: String(valuesToSend[props.formData.companyName]),
          position: String(valuesToSend[props.formData.position]),
          street: String(valuesToSend[props.formData.street]),
          postalCode: String(valuesToSend[props.formData.postalCode]),
          city: String(valuesToSend[props.formData.city]),
          id: props.training.id,
          startDate: props.training.startDate,
          endDate: props.training.endDate
        };

        await fetch(gcpTrainingRequestsWriterEndpoint, {
          method: "POST",
          body: JSON.stringify(trainingRequest),
          headers
        });
      }

      navigate(props.trainingDetailsPageUrl, {
        state: { showResultsModal: true },
        replace: true
      });
    } catch (error) {
      logger.error({ message: (error as Error).message });
      props.setIsSubmitting(false);
    }
  };

  return (
    <Section
      backgroundColor="white"
      data-testid="training-registration-form-section"
      spacing={isPreviewMode ? "none" : "default"}
    >
      <Form onSubmit={handleFormSubmit} rightAlignButton>
        <Grid container>
          <Grid xs={12} lg={8}>
            <FormFields
              isSubmitting={props.isSubmitting}
              formData={props.formData}
              setFormStatus={props.setFormStatus}
              submitButtonRedirectUrl={props.trainingDetailsPageUrl}
            />
            <StyledRecaptchaText testId="training-registration-page-recaptcha" />
          </Grid>
        </Grid>
      </Form>
    </Section>
  );
};

export default TrainingRegistrationForm;
